/**
 * `/settings/*` (#581): the h1 "Settings", a plain vertical sub-menu on the start side and the chosen page beside it.
 *
 * The sub-menu is a list of links, not tiles; the current page carries `aria-current="page"` and a bar on its start
 * side (never colour alone, §7). The pages below own an h2 for their title: this layout owns the page's only h1.
 * At phone width the menu stacks above the content (settings.css).
 *
 * Worked solutions are deliberately not an entry: they are not an app screen (#586).
 */

import { useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router';
import { useIsPhone } from '../../app/use-is-phone.js';
import { useMessages } from '../../i18n/locale.js';
import { defineMessages } from '../../i18n/messages.js';

const messages = defineMessages({
  en: {
    title: 'Settings',
    back: 'Settings',
    account: 'Account',
    accountSub: 'Sign-in, name, Moodle',
    courses: 'Courses and colours',
    coursesSub: 'What shows, how it looks',
    consent: 'Consent',
    consentSub: 'Three purposes',
    data: 'My data',
    dataSub: 'See or delete',
    contact: 'Contact us',
    contactSub: 'Questions, problems, your data',
  },
  he: {
    title: 'הגדרות',
    back: 'הגדרות',
    account: 'החשבון',
    accountSub: 'כניסה, שם, Moodle',
    courses: 'קורסים וצבעים',
    coursesSub: 'מה מוצג ואיך',
    consent: 'הסכמה',
    consentSub: 'שלוש מטרות',
    data: 'הנתונים שלי',
    dataSub: 'צפייה או מחיקה',
    contact: 'יצירת קשר',
    contactSub: 'שאלות, תקלות, הנתונים',
  },
});

const ENTRIES = [
  { to: '/settings/account', label: 'account', sub: 'accountSub' },
  { to: '/settings/courses', label: 'courses', sub: 'coursesSub' },
  { to: '/settings/consent', label: 'consent', sub: 'consentSub' },
  { to: '/settings/data', label: 'data', sub: 'dataSub' },
  { to: '/settings/contact', label: 'contact', sub: 'contactSub' },
] as const;

export function SettingsLayout(): React.JSX.Element {
  const t = useMessages(messages);
  const { hash, pathname } = useLocation();
  // The router does not scroll to `#id` itself. The redirects from `/setup` and Exam prep's "add an assessment" link
  // land here with a hash (`#moodle-connection`, `#assessments`), and the targets are rendered by the time this runs.
  useEffect(() => {
    if (hash === '') return;
    let id: string;
    try {
      id = decodeURIComponent(hash.slice(1));
    } catch {
      // A malformed fragment (`#%`) names nothing; stay where we are.
      return;
    }
    document.getElementById(id)?.scrollIntoView?.();
  }, [hash, pathname]);
  const isPhone = useIsPhone();
  // On a phone `/settings` is the list screen and each page is its own screen with a way back (SettingsMenuPhone).
  const atRoot = pathname.replace(/\/+$/, '') === '/settings';
  const showMenu = !isPhone || atRoot;
  const showContent = !isPhone || !atRoot;
  return (
    <div className="page settings-layout">
      <h1 className={isPhone && !atRoot ? 'visually-hidden' : undefined}>{t.title}</h1>
      <div className="settings-body">
        {showMenu ? (
        <nav className="settings-menu" aria-label={t.title}>
          <ul>
            {ENTRIES.map((entry) => (
              <li key={entry.to}>
                <NavLink to={entry.to} end>
                  <span className="settings-menu-text">
                    <span className="settings-menu-label">{t[entry.label]}</span>
                    <span className="settings-menu-sub">{t[entry.sub]}</span>
                  </span>
                  <ChevronIcon />
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        ) : null}
        {showContent ? (
          <div className="settings-content">
            {isPhone ? (
              <Link className="settings-back" to="/settings">
                <ChevronIcon />
                <span>{t.back}</span>
              </Link>
            ) : null}
            <Outlet />
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** The phone list's chevron; it points away from the start edge (CSS flips it in RTL). The back link reuses it, turned. */
function ChevronIcon(): React.JSX.Element {
  return (
    <svg className="settings-chevron" aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}
